import { SITE_LAYOUT } from 'config/siteLayout';
import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import Submission from './Submission';
import { MY_SUBMISSION_BY_ORDER_NUMBER } from 'lib/graphql/queries/submissions';
import { Submission as SubmissionType } from 'types/submissions';
import withLayout from 'hocs/withLayout';

/**
 * SubmissionWithData Component
 *
 * Container component that fetches submission data by order number and handles
 * loading and error states. Wraps the Submission component with Apollo Client
 * query and data fetching logic.
 *
 * Features:
 * - Fetches submission data using order number from route query params.
 * - Displays loading state with CircularProgress.
 * - Handles error states with retry functionality.
 * - Scrolls to top on mount.
 *
 * Used in:
 * - `pages/account/submissions/[submission].tsx` page route.
 *
 * @returns Submission layout with data fetching and error handling
 */
const SubmissionWithData = (): React.JSX.Element => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  const router = useRouter();
  const { submission: orderNumber } = router.query;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data, loading, error, refetch } = useQuery<{
    mySubmissionByOrderNumber: SubmissionType | null;
  }>(MY_SUBMISSION_BY_ORDER_NUMBER, {
    notifyOnNetworkStatusChange: true,
    variables: { orderNumber: orderNumber as string },
    fetchPolicy: 'network-only',
    skip: !router.isReady || !orderNumber,
  });

  /**
   * Handles retry of the submission query
   */
  const handleRetryQuery = (): void => {
    refetch();
  };

  const submission = data?.mySubmissionByOrderNumber ?? null;
  // Avoid rendering edit form with partial cache data while network request is in-flight.
  const isLoading = loading;

  /**
   * Renders the loading state with a circular progress indicator
   *
   * @returns Loading state component
   */
  const renderLoadingState = (): React.JSX.Element => (
    <Box
      width="100%"
      minHeight="70vh"
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
    >
      <CircularProgress />
    </Box>
  );

  /**
   * Renders the error state with retry functionality
   *
   * @returns Error state component with retry button
   */
  const renderErrorState = (): React.JSX.Element => (
    <Box
      minHeight="70vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <Typography variant="h5" gutterBottom>
        Oh snap!
      </Typography>
      <Box maxWidth={mobile ? '80%' : '50%'}>
        <Typography variant="body1" align="center" gutterBottom color="textSecondary">
          Something went wrong while retrieving this submission.
        </Typography>
      </Box>
      <Box paddingTop={2}>
        <Button variant="text" color="primary" onClick={handleRetryQuery}>
          Retry
        </Button>
      </Box>
    </Box>
  );

  // Handle case where submission is null (not found or not loaded)
  if (!submission && !isLoading && !error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center">
        <Box maxWidth={SITE_LAYOUT.contentMaxWidth} width="100%">
          <Box
            minHeight="70vh"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="h5" gutterBottom>
              Submission Not Found
            </Typography>
            <Box maxWidth={mobile ? '80%' : '50%'}>
              <Typography
                variant="body1"
                align="center"
                gutterBottom
                color="textSecondary"
              >
                The submission you are looking for could not be found.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box maxWidth={SITE_LAYOUT.contentMaxWidth} width="100%">
        {isLoading ? (
          renderLoadingState()
        ) : error ? (
          renderErrorState()
        ) : submission ? (
          <Submission submission={submission} />
        ) : null}
      </Box>
    </Box>
  );
};

export default withLayout(SubmissionWithData, {
  contentWidth: 'sm',
  margin: true,
});
